import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import '../../style/dashboard.css';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SlipBoothDashboard = () => {
    const navigate = useNavigate();
    
    // Get App ID, Total Voters, Language, and Booth No from context
    const { appId, total_voters, language, boothNo } = useAppId(); 
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Slip Distribution Dashboard",
            subtitle: "Overview of digital and physical slip distribution progress.",
            totalVoters: "Total Voters",
            totalSlipsSent: "Total Slips Sent",
            sent: "Sent",
            pendingSlips: "Pending Slips :",
            whatsapp: "WhatsApp",
            web: "Web",
            sms: "SMS",
            print: "Print",
            distributionReports: "DISTRIBUTION REPORTS",
            phonebookSlipSend: "My Phonebook Slip Send", // Updated label
            boothWiseSlip: "Booth Wise Slip",
            phonebookWiseSlip: "My Slip Sending", // Updated label
            pollingLocationWiseSlip: "Polling Location Wise Slip",
            VolunteersSlipSend: "Volunteers Slip Sending",
            dateWiseSlip: "Date Wise Slip",
            masterSearch: "Master Search"
        },
        hi: {
            title: "स्लिप वितरण डैशबोर्ड",
            subtitle: "डिजिटल और भौतिक स्लिप वितरण प्रगति का अवलोकन।",
            totalVoters: "कुल मतदाता",
            totalSlipsSent: "कुल स्लिप भेजी गई",
            sent: "भेजी गई",
            pendingSlips: "लंबित स्लिप :",
            whatsapp: "व्हाट्सएप",
            web: "वेब",
            sms: "एसएमएस",
            print: "प्रिंट",
            distributionReports: "वितरण रिपोर्ट",
            phonebookSlipSend: "मेरी फोनबुक स्लिप भेजें", // Updated label
            boothWiseSlip: "बूथ वार स्लिप",
            phonebookWiseSlip: "मेरी स्लिप भेजना", // Updated label
            pollingLocationWiseSlip: "पोलिंग लोकेशन वार स्लिप",
            VolunteersSlipSend: "मतदाता स्लिप भेजना",
            dateWiseSlip: "तिथि वार स्लिप",
            masterSearch: "मास्टर सर्च"
        },
        gu: {
            title: "સ્લિપ વિતરણ ડેશબોર્ડ",
            subtitle: "ડિજિટલ અને ભૌતિક સ્લિપ વિતરણ પ્રગતિની ઝાંખી.",
            totalVoters: "કુલ મતદારો",
            totalSlipsSent: "કુલ સ્લિપ મોકલાઈ",
            sent: "મોકલાઈ",
            pendingSlips: "બાકી સ્લિપ :",
            whatsapp: "વોટ્સએપ",
            web: "વેબ",
            sms: "એસએમએસ",
            print: "પ્રિંટ",
            distributionReports: "વિતરણ રિપોર્ટ્સ",
            phonebookSlipSend: "મારી ફોનબુક સ્લિપ મોકલો", // Updated label
            boothWiseSlip: "બૂથ વાઈઝ સ્લિપ",
            phonebookWiseSlip: "મારી સ્લિપ મોકલવી", // Updated label
            pollingLocationWiseSlip: "પોલિંગ લોકેશન વાઈઝ સ્લિપ",
            VolunteersSlipSend: "મતદાર સ્લિપ મોકલવી",
            dateWiseSlip: "તારીખ વાઈઝ સ્લિપ",
            masterSearch: "માસ્ટર શોધ"
        }
    };

    const txt = t[lang];
    
    
    // Initial State for Slip Distribution
    const [data, setData] = useState({
        totalVoters: 0,
        totalSlipsSent: 0,
        pieData: [
            { name: txt.whatsapp, value: 0, color: '#10b981' }, 
            { name: txt.web, value: 0, color: '#3b82f6' },      
            { name: txt.sms, value: 0, color: '#f59e0b' },      
            { name: txt.print, value: 0, color: '#6366f1' },    
        ],
        pending: 0,
    });

    // Update pieData when language changes
    useEffect(() => {
        setData(prev => ({
            ...prev,
            pieData: [
                { name: txt.whatsapp, value: prev.pieData[0]?.value || 0, color: '#10b981' },
                { name: txt.web, value: prev.pieData[1]?.value || 0, color: '#3b82f6' },
                { name: txt.sms, value: prev.pieData[2]?.value || 0, color: '#f59e0b' },
                { name: txt.print, value: prev.pieData[3]?.value || 0, color: '#6366f1' },
            ]
        }));
    }, [lang, txt.whatsapp, txt.web, txt.sms, txt.print]);

    // Fallbacks to LocalStorage if context drops
    const currentAppId = appId || localStorage.getItem('appId') || "";
    const currentBoothNo = boothNo || localStorage.getItem('boothNo') || "";
    const currentTotalVoters = total_voters || localStorage.getItem('totalVoters') || 0;

    useEffect(() => {
        if (currentAppId) {
            fetchData();
        }
    }, [currentAppId, currentBoothNo]);

    const fetchData = async () => {
        try {
            // Guarantee variables are sent as strings per the API requirement
            const payload = {
                app_id: String(currentAppId),
                booth_no: String(currentBoothNo)
            };

            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_total_slip_distribution_count`, payload);

            // Parsing exact JSON structure you provided
            if (response.data.success && response.data.data && response.data.data.Success === "1") {
                const result = (response.data.data.result && response.data.data.result.length > 0) ? response.data.data.result[0] : {};
                const result2 = (response.data.data.result2 && response.data.data.result2.length > 0) ? response.data.data.result2[0] : {};

                // Extract breakdown counts exactly as written in API response
                const whatsappCount = Number(result.whtsapp) || 0; // matching 'whtsapp' from JSON
                const webCount = Number(result.web) || 0;
                const smsCount = Number(result.sms) || 0;
                const printCount = Number(result.print) || 0;

                // Calculate Totals
                const totalSlipsSent = whatsappCount + webCount + smsCount + printCount;
                
                // Extract total voters safely from result2
                const apiTotalVoters = result2.total_voter !== undefined ? Number(result2.total_voter) : Number(currentTotalVoters);
                
                // Calculate Pending (Not Sent)
                const pending = apiTotalVoters > totalSlipsSent ? apiTotalVoters - totalSlipsSent : 0;

                setData({
                    totalVoters: apiTotalVoters,
                    totalSlipsSent: totalSlipsSent,
                    pieData: [
                        { name: txt.whatsapp, value: whatsappCount, color: '#10b981' },
                        { name: txt.web, value: webCount, color: '#3b82f6' },
                        { name: txt.sms, value: smsCount, color: '#f59e0b' },
                        { name: txt.print, value: printCount, color: '#6366f1' },
                    ],
                    pending: pending,
                });
            }
        } catch (error) {
            console.error("Error fetching slip distribution data:", error);
        }
    };

    // --- Updated navigate handler to accept state ---
    const handleNavigate = (path, stateObj = null) => {
        if (stateObj) {
            navigate(path, { state: stateObj });
        } else {
            navigate(path);
        }
    };

    // --- SLIP OPTIONS LIST (UPDATED with route state for user_id) ---
    const slipOptions = [
        { 
            title: txt.phonebookSlipSend, 
            icon: '📒', 
            onClick: () => handleNavigate('/phonebook_wise_slip_sending', { user_id: localStorage.getItem('userId') || '' }) 
        },
        { title: txt.phonebookWiseSlip, icon: '📞', onClick: () => handleNavigate('/dis_my_slip_sending_voter') },
        { title: txt.boothWiseSlip, icon: '🏪', onClick: () => handleNavigate('/booth_wise_slip_sending', { booth_no: localStorage.getItem('boothNo') || '' }) }
    ];

    // Custom Label Render for Donut Chart
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (!percent || percent === 0) return null; // Safe check for 0 values
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 20;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="#334155" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12" fontWeight="600">
                {`${(percent * 100).toFixed(1)}%`}
            </text>
        );
    };

    return (
        <div className="app-container dashboard-page">
            <div className="dashboard-content container-fluid p-4">

                <Header title={txt.title} />

                <div className="row g-4 mb-5 fade-in-up mt-1" style={{ animationDelay: '0.2s' }}>

                    {/* --- Left Side: Stats and Chart --- */}
                    <div className="col-lg-5 col-md-12">
                        <div className="survey-card h-90 d-flex flex-column justify-content-between">
                            <div className="top-stats-row">
                                <div className="stat-box">
                                    <span className="stat-label">{txt.totalVoters}</span>
                                    <span className="stat-value text-dark">{data.totalVoters.toLocaleString()}</span>
                                </div>
                                <div className="stat-divider"></div>
                                <div className="stat-box">
                                    <span className="stat-label">{txt.totalSlipsSent}</span>
                                    <span className="stat-value text-dark">{data.totalSlipsSent.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Chart Area */}
                            <div className="chart-wrapper">
                                {data.totalSlipsSent === 0 ? (
                                    /* Show empty gray donut if nothing is sent yet to prevent Recharts from looking broken */
                                    <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <div style={{ width: '220px', height: '220px', borderRadius: '50%', border: '25px solid #f1f5f9' }}></div>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie
                                                data={data.pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={80}
                                                outerRadius={110}
                                                paddingAngle={0}
                                                dataKey="value"
                                                startAngle={90}
                                                endAngle={-270}
                                                label={renderCustomizedLabel}
                                                labelLine={true}
                                            >
                                                {data.pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value) => value.toLocaleString()}
                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}

                                {/* Center Text */}
                                <div className="chart-center-text">
                                    <span className="survey-percentage">
                                        {data.totalVoters > 0 ? ((data.totalSlipsSent / data.totalVoters) * 100).toFixed(1) : 0}%
                                    </span>
                                    <span className="survey-label">{txt.sent}</span>
                                </div>
                            </div>

                            {/* Pending/Remaining Alert */}
                            <div className="unavailable-pill" style={{ backgroundColor: '#f1f5f9', color: '#475569', borderColor: '#e2e8f0' }}>
                                {txt.pendingSlips} <strong>{data.pending.toLocaleString()}</strong>
                            </div>

                            {/* Bottom Stats Grid */}
                            <div className="bottom-stats-grid">
                                {data.pieData.map((entry, index) => (
                                    <div key={index} className={`bottom-stat-item ${index !== data.pieData.length - 1 ? 'border-right' : ''}`}>
                                        <span className="b-stat-value" style={{ color: entry.color }}>
                                            {entry.value.toLocaleString()}
                                        </span>
                                        <span className="b-stat-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- Right Side: Overall Slip Options --- */}
                    <div className="col-lg-7 col-md-12">
                        <div className="survey-card">
                            <div className="pb-4">
                                <h4 className="section-title">{txt.distributionReports}</h4>
                            </div>

                            <div className="row g-3">
                                {slipOptions.map((option, index) => (
                                    <div key={index} className="col-6 col-md-4">
                                        <div className="option-card" onClick={option.onClick}>
                                            <div className="option-icon">{option.icon}</div>
                                            <span className="option-text">{option.title}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .survey-card {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.03);
                    border: 1px solid #f1f5f9;
                }
                .top-stats-row {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .stat-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 120px;
                }
                .stat-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                .stat-value {
                    font-size: 20px;
                    font-weight: 800;
                }
                .stat-divider {
                    width: 1px;
                    height: 40px;
                    background-color: #e2e8f0;
                    margin: 0 20px;
                }
                .chart-wrapper {
                    position: relative;
                    margin: 10px 0 25px 0;
                }
                .chart-center-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    pointer-events: none;
                }
                .survey-percentage {
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                }
                .survey-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    margin-top: 4px;
                }
                .unavailable-pill {
                    font-weight: 500;
                    font-size: 14px;
                    text-align: center;
                    padding: 8px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                    border: 1px solid;
                }
                .bottom-stats-grid {
                    display: flex;
                    justify-content: space-between;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 15px;
                }
                .bottom-stat-item {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .border-right {
                    border-right: 1px solid #e2e8f0;
                }
                .b-stat-value {
                    font-size: 18px;
                    font-weight: 800;
                    margin-bottom: 2px;
                }
                .b-stat-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 600;
                }
                .section-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #475569;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin: 0;
                }
                .option-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 25px 15px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .option-card:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
                    transform: translateY(-2px);
                }
                .option-icon {
                    font-size: 28px;
                    margin-bottom: 12px;
                }
                .option-text {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    text-align: center;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );
};

export default SlipBoothDashboard;